import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Search, Sparkles, Zap, LayoutGrid, Send, Check, X, Filter,
  RotateCcw, Loader2, Moon, Sun, Building2, Trash2, Download, BookmarkPlus, Settings as SettingsIcon, MailCheck, Map as MapIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api, exportUrl, CATEGORY_GROUPS, AREAS, DISTANCES, RADII, RELEVANCES } from "@/lib/api";
import { LeadCard } from "@/components/LeadCard";
import { MapView } from "@/components/MapView";
import { AreaPicker } from "@/components/AreaPicker";
import { CreditsBar } from "@/components/CreditsBar";
import { GmailBar } from "@/components/GmailBar";
import { SearchProgress } from "@/components/SearchProgress";

const TABS = [
  { key: "alle", label: "ALLE", testid: "status-tab-all" },
  { key: "ikke_behandlet", label: "IKKE BEHANDLET", testid: "status-tab-unprocessed" },
  { key: "godkjent", label: "GODKJENT", testid: "status-tab-approved" },
  { key: "avvist", label: "AVVIST", testid: "status-tab-rejected" },
];

export default function Prospects() {
  const [dark, setDark] = useState(false);
  const [areaSel, setAreaSel] = useState({ label: "Asker", center: [59.8333, 10.4347], radius: 10 });
  const [count, setCount] = useState(20);
  const [creditsKey, setCreditsKey] = useState(0);
  const [cr, setCr] = useState(null);
  const [tier, setTier] = useState("standard");
  const maxCount = !cr ? 20 : tier === "pro" ? cr.pro_limit : (!cr.unlimited && cr.credits <= 0 ? Math.min(cr.free_limit, cr.standard_limit) : cr.standard_limit);
  const costText = !cr ? "" : cr.unlimited ? "Ubegrenset (eier) – ingen kreditt trekkes"
    : cr.credits > 0 ? `Koster 1 kreditt (du har ${cr.credits})` : cr.free_searches_left > 0 ? `Bruker 1 av ${cr.free_searches_left} gratis søk` : "Ingen søk igjen – kjøp kreditter";
  const [source, setSource] = useState("places");
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [generating, setGenerating] = useState(false);

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ alle: 0, ikke_behandlet: 0, godkjent: 0, avvist: 0, godkjent_usendt: 0 });
  const [tab, setTab] = useState("alle");
  const [quick, setQuick] = useState(false);
  const [view, setView] = useState("liste");

  const [fIndustries, setFIndustries] = useState([]);
  const [fArea, setFArea] = useState("Alle");
  const [fDistance, setFDistance] = useState("Alle");
  const [fRadius, setFRadius] = useState("Alle");
  const [fRelevance, setFRelevance] = useState("Alle");
  const [q, setQ] = useState("");

  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [gmail, setGmail] = useState(null);
  const [savedSearches, setSavedSearches] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  const loadStats = useCallback(async () => setStats(await api.stats()), []);
  const loadGmail = useCallback(async () => setGmail(await api.gmailStatus()), []);
  const loadSaved = useCallback(async () => setSavedSearches(await api.listSaved()), []);

  const loadLeads = useCallback(async () => {
    const params = {};
    if (tab !== "alle") params.status = tab;
    if (fIndustries.length) params.industries = fIndustries.join(",");
    if (fArea !== "Alle") params.area = fArea;
    if (fDistance !== "Alle") params.distance = fDistance;
    if (fRadius !== "Alle") params.radius = parseInt(fRadius, 10);
    if (fRelevance !== "Alle") params.relevance = fRelevance;
    if (q) params.q = q;
    setLeads(await api.listLeads(params));
  }, [tab, fIndustries, fArea, fDistance, fRadius, fRelevance, q]);

  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { loadStats(); loadGmail(); loadSaved(); }, [loadStats, loadGmail, loadSaved]);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("gmail") === "connected") {
      toast.success("Gmail tilkoblet!");
      window.history.replaceState({}, "", window.location.pathname);
      loadGmail();
    }
  }, [loadGmail]);

  const toggleIndustry = (list, setList, val) =>
    setList(list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.generate({
        area: areaSel.label, lat: areaSel.center[0], lng: areaSel.center[1], radius_km: areaSel.radius,
        industries: selectedIndustries, count: Math.min(Number(count), maxCount), source, tier,
        request_id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      });
      toast.success(`Fant ${res.count} potensielle kunder${res.charged ? " – 1 søk trukket" : ""}`);
      setCreditsKey((k) => k + 1);
      await Promise.all([loadLeads(), loadStats()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke finne bedrifter");
    } finally {
      setGenerating(false);
    }
  };

  const setStatus = async (id, status) => {
    const updated = await api.setStatus(id, status);
    setLeads((prev) => prev.map((l) => (l.id === id ? updated : l)));
    loadStats();
    toast.success(status === "godkjent" ? "Godkjent" : status === "avvist" ? "Avvist" : "Tilbakestilt");
    if (tab !== "alle") setTimeout(loadLeads, 300);
  };

  const onEmailChange = (updated) =>
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));

  const toggleSelect = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const allVisibleSelected = leads.length > 0 && leads.every((l) => selected.has(l.id));
  const toggleSelectAll = () =>
    setSelected(allVisibleSelected ? new Set() : new Set(leads.map((l) => l.id)));

  const bulk = async (status) => {
    const ids = [...selected];
    if (!ids.length) return;
    await api.bulk(ids, status);
    toast.success(`${status === "godkjent" ? "Godkjente" : "Avviste"} ${ids.length} bedrifter`);
    setSelected(new Set());
    await Promise.all([loadLeads(), loadStats()]);
  };

  const syncSent = async () => {
    setSyncing(true);
    try {
      const res = await api.syncSent();
      toast.success(`Fant ${res.count} tidligere mottakere i Gmail – duplikater merkes nå`);
      await loadLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lese Gmail");
    } finally {
      setSyncing(false);
    }
  };

  const doSend = async () => {
    setSending(true);
    try {
      const res = await api.send(null);
      const skip = res.skipped ? `, ${res.skipped} hoppet over (allerede kontaktet)` : "";
      toast.success(
        res.mode === "live"
          ? `${res.sent} e-poster sendt fra ${res.sender}${skip}`
          : `${res.sent} e-poster sendt (simulert modus)${skip}`
      );
      setSendOpen(false);
      await Promise.all([loadLeads(), loadStats()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sending feilet");
    } finally {
      setSending(false);
    }
  };

  const clearAll = async () => {
    await api.clear();
    setSelected(new Set());
    await Promise.all([loadLeads(), loadStats()]);
    toast.success("Listen tømt");
  };

  const saveSearch = async () => {
    if (!saveName.trim()) { toast.error("Gi søket et navn"); return; }
    await api.createSaved({
      name: saveName.trim(), area: areaSel.label, lat: areaSel.center[0], lng: areaSel.center[1], radius_km: areaSel.radius,
      industries: selectedIndustries, count: Number(count), source,
    });
    setSaveName("");
    setSaveOpen(false);
    await loadSaved();
    toast.success("Søk lagret");
  };

  const applySaved = (s) => {
    if (s.lat != null && s.lng != null) {
      setAreaSel({ label: s.area, center: [s.lat, s.lng], radius: s.radius_km || 10 });
    } else {
      api.geocode(s.area.split(" + ")[0]).then((g) => setAreaSel({ label: s.area, center: [g.lat, g.lng], radius: 10 })).catch(() => {});
    }
    setSelectedIndustries(s.industries || []);
    setCount(s.count || 20);
    setSource(s.source || "places");
    toast.success(`Lastet «${s.name}»`);
  };

  const removeSaved = async (id) => {
    await api.deleteSaved(id);
    await loadSaved();
  };

  const sendCount = stats.godkjent_usendt || 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header
        data-testid="app-header"
        className="sticky top-0 z-30 border-b border-border bg-card/90 backdrop-blur-md"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-extrabold tracking-tight leading-none">
                Potensielle kunder
              </h1>
              <p className="text-xs text-muted-foreground">Ask-Stokke Renhold · AI-prospektering</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <CreditsBar refreshKey={creditsKey} onData={setCr} />
            <GmailBar status={gmail} refresh={loadGmail} />
            <Link to="/pro/innstillinger" data-testid="open-settings-button">
              <Button size="icon" variant="outline" title="Innstillinger">
                <SettingsIcon className="h-4 w-4" />
              </Button>
            </Link>
            <Button
              data-testid="theme-toggle-button"
              size="icon"
              variant="outline"
              onClick={() => setDark((d) => !d)}
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Search / generation panel */}
        <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <AreaPicker value={areaSel} onChange={setAreaSel} />
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="tier-toggle">
            {[["standard", `Standard · opptil ${cr?.standard_limit ?? 20}`], ["pro", `Pro · opptil ${cr?.pro_limit ?? 100}`]].map(([k, l]) => (
              <button key={k} type="button" data-testid={`tier-${k}`} disabled={k === "pro" && !cr?.pro}
                onClick={() => { setTier(k); setCount(k === "pro" ? cr.pro_limit : Math.min(20, maxCount)); }}
                className={`rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors disabled:opacity-40 ${tier === k ? "border-primary bg-primary text-primary-foreground" : "border-border bg-secondary"}`}>{l}</button>
            ))}
            {!cr?.pro && <span className="text-xs text-muted-foreground">Pro låses opp ved kjøp av 300, 600 eller 1200 kreditter</span>}
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-[200px_auto] gap-4 items-end">
            <div>
              <Label className="text-sm font-semibold">Antall bedrifter (maks {maxCount})</Label>
              <Input
                data-testid="lead-count-input"
                type="number"
                min={1}
                max={maxCount}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                className="mt-1 bg-background"
              />
            </div>
            <Button
              data-testid="find-businesses-button"
              onClick={generate}
              disabled={generating}
              size="lg"
              className="gap-2 h-11 font-bold"
            >
              {generating ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
              {generating ? "FINNER BEDRIFTER..." : "FINN BEDRIFTER"}
            </Button>
            <p className="sm:col-span-2 text-xs text-muted-foreground" data-testid="search-cost">{costText}. Søk uten treff eller med feil er gratis.</p>
            {generating && <SearchProgress />}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="source-toggle">
            <span className="text-xs font-semibold text-muted-foreground">Datakilde:</span>
            <div className="inline-flex rounded-lg border border-border bg-secondary p-0.5">
              <button
                data-testid="source-places-button"
                onClick={() => setSource("places")}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${source === "places" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
              >
                🌐 Ekte bedrifter (Google)
              </button>
              <button
                data-testid="source-ai-button"
                onClick={() => setSource("ai")}
                className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${source === "ai" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
              >
                ✨ AI-genererte
              </button>
            </div>
            {source === "places" && (
              <span className="text-xs text-muted-foreground">Ekte navn, adresse, nettside &amp; telefon fra Google. E-post hentes fra nettsiden når mulig.</span>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setSaveOpen(true)} data-testid="save-search-button" className="gap-1.5">
              <BookmarkPlus className="h-4 w-4" /> Lagre søk
            </Button>
            {savedSearches.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-muted-foreground">Lagrede søk:</span>
                {savedSearches.map((s) => (
                  <span key={s.id} className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary pl-2.5 pr-1 py-0.5 text-xs">
                    <button onClick={() => applySaved(s)} data-testid={`saved-search-${s.name}`} className="font-medium hover:text-primary">{s.name}</button>
                    <button onClick={() => removeSaved(s.id)} className="rounded-full p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4" data-testid="industry-selector">
            <Label className="text-sm font-semibold flex items-center gap-1.5">
              <Building2 className="h-4 w-4" /> Bransjer <span className="text-muted-foreground font-normal">(tom = alle relevante)</span>
            </Label>
            <div className="mt-3 space-y-3">
              {CATEGORY_GROUPS.map((grp) => (
                <div key={grp.group} data-testid={`industry-group-${grp.group}`}>
                  <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground/80">
                    {grp.group}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {grp.items.map((c) => {
                      const active = selectedIndustries.includes(c);
                      return (
                        <button
                          key={c}
                          data-testid={`industry-chip-${c}`}
                          onClick={() => toggleIndustry(selectedIndustries, setSelectedIndustries, c)}
                          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                            active
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border bg-secondary text-secondary-foreground hover:border-primary/50"
                          }`}
                        >
                          {c}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Status tabs + mode + send */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1.5 rounded-xl border border-border bg-card p-1.5">
            {TABS.map((t) => (
              <button
                key={t.key}
                data-testid={t.testid}
                onClick={() => setTab(t.key)}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors flex items-center gap-1.5 ${
                  tab === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
                }`}
              >
                {t.label}
                <Badge variant="secondary" className={`h-5 min-w-5 justify-center px-1 ${tab === t.key ? "bg-primary-foreground/20 text-primary-foreground" : ""}`}>
                  {stats[t.key]}
                </Badge>
              </button>
            ))}
          </div>

          <Button
            data-testid="quick-mode-toggle-button"
            variant={quick ? "default" : "outline"}
            onClick={() => setQuick((v) => !v)}
            className="gap-1.5"
          >
            {quick ? <LayoutGrid className="h-4 w-4" /> : <Zap className="h-4 w-4" />}
            {quick ? "KORTVISNING" : "⚡ HURTIGMODUS"}
          </Button>

          <div className="inline-flex rounded-xl border border-border bg-card p-1" data-testid="view-toggle">
            <button
              data-testid="view-list-button"
              onClick={() => setView("liste")}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${view === "liste" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
            >
              <LayoutGrid className="h-4 w-4" /> Liste
            </button>
            <button
              data-testid="view-map-button"
              onClick={() => setView("kart")}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${view === "kart" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}
            >
              <MapIcon className="h-4 w-4" /> Kart
            </button>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {stats.godkjent > 0 && (
              <Button
                data-testid="export-csv-button"
                variant="outline"
                size="sm"
                onClick={() => window.open(exportUrl("godkjent"), "_blank")}
                className="gap-1.5"
              >
                <Download className="h-4 w-4" /> Eksporter CSV
              </Button>
            )}
            {leads.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearAll} className="gap-1.5 text-muted-foreground" data-testid="clear-all-button">
                <Trash2 className="h-4 w-4" /> Tøm
              </Button>
            )}
            <Button
              data-testid="sync-sent-button"
              variant="outline"
              onClick={syncSent}
              disabled={syncing || !gmail?.connected}
              className="gap-1.5"
              title="Les hvem du allerede har sendt til i Gmail"
            >
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <MailCheck className="h-4 w-4" />} Sjekk Gmail
            </Button>
            <Button
              data-testid="global-send-emails-button"
              onClick={() => setSendOpen(true)}
              disabled={sendCount === 0}
              size="lg"
              className="gap-2 font-bold bg-[#16A34A] hover:bg-[#15803d] text-white disabled:opacity-40"
            >
              <Send className="h-5 w-5" /> SEND {sendCount} E-POSTER
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="rounded-xl border border-border bg-card p-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mr-1">
            <Filter className="h-4 w-4" /> Filter
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="filter-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Søk navn / e-post..."
              className="h-9 w-48 pl-8 bg-background"
            />
          </div>
          <Select value={fArea} onValueChange={setFArea}>
            <SelectTrigger data-testid="filter-area-dropdown" className="h-9 w-36 bg-background"><SelectValue placeholder="Område" /></SelectTrigger>
            <SelectContent>{AREAS.map((a) => <SelectItem key={a} value={a}>{a === "Alle" ? "Alle områder" : a}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={fDistance} onValueChange={setFDistance}>
            <SelectTrigger data-testid="filter-distance-dropdown" className="h-9 w-32 bg-background"><SelectValue placeholder="Avstand" /></SelectTrigger>
            <SelectContent>{DISTANCES.map((d) => <SelectItem key={d} value={d}>{d === "Alle" ? "Alle avstander" : d}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={fRadius} onValueChange={setFRadius}>
            <SelectTrigger data-testid="filter-radius-dropdown" className="h-9 w-32 bg-background"><SelectValue placeholder="Radius" /></SelectTrigger>
            <SelectContent>{RADII.map((r) => <SelectItem key={r} value={r}>{r === "Alle" ? "Alle radius" : `Innen ${r}`}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={fRelevance} onValueChange={setFRelevance}>
            <SelectTrigger data-testid="filter-relevance-dropdown" className="h-9 w-32 bg-background"><SelectValue placeholder="Relevans" /></SelectTrigger>
            <SelectContent>{RELEVANCES.map((r) => <SelectItem key={r} value={r}>{r === "Alle" ? "All relevans" : r}</SelectItem>)}</SelectContent>
          </Select>

          <Select onValueChange={(v) => toggleIndustry(fIndustries, setFIndustries, v)}>
            <SelectTrigger data-testid="filter-industry-dropdown" className="h-9 w-40 bg-background">
              <SelectValue placeholder={fIndustries.length ? `${fIndustries.length} bransjer` : "Bransje"} />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              {CATEGORY_GROUPS.map((grp) => (
                <SelectGroup key={grp.group}>
                  <SelectLabel className="text-[11px] uppercase tracking-wider text-muted-foreground/80">{grp.group}</SelectLabel>
                  {grp.items.map((c) => (
                    <SelectItem key={c} value={c}>
                      {fIndustries.includes(c) ? "✓ " : ""}{c}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
          {(fIndustries.length || fArea !== "Alle" || fDistance !== "Alle" || fRadius !== "Alle" || fRelevance !== "Alle" || q) ? (
            <Button variant="ghost" size="sm" className="h-9 gap-1.5" onClick={() => {
              setFIndustries([]); setFArea("Alle"); setFDistance("Alle"); setFRadius("Alle"); setFRelevance("Alle"); setQ("");
            }}>
              <RotateCcw className="h-3.5 w-3.5" /> Nullstill
            </Button>
          ) : null}

          {leads.length > 0 && (
            <label className="ml-auto flex items-center gap-2 text-xs font-medium cursor-pointer">
              <Checkbox data-testid="select-all-checkbox" checked={allVisibleSelected} onCheckedChange={toggleSelectAll} />
              Velg alle ({leads.length})
            </label>
          )}
        </div>

        {/* Leads grid / map */}
        {view === "kart" ? (
          <MapView leads={leads} onStatus={setStatus} onToggleEmail={(id) => setExpanded(expanded === id ? null : id)} />
        ) : leads.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card py-20 text-center">
            <Building2 className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="mt-3 font-heading font-semibold text-foreground">Ingen bedrifter enda</p>
            <p className="text-sm text-muted-foreground">Skriv inn et område og trykk «FINN BEDRIFTER».</p>
          </div>
        ) : (
          <div className={quick
            ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
            : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"}>
            <AnimatePresence>
              {leads.map((lead) => (
                <LeadCard
                  key={lead.id}
                  lead={lead}
                  compact={quick}
                  selected={selected.has(lead.id)}
                  onSelect={toggleSelect}
                  onStatus={setStatus}
                  expanded={expanded === lead.id}
                  onToggleEmail={(id) => setExpanded(expanded === id ? null : id)}
                  onChange={onEmailChange}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      {/* Bulk action bar */}
      <AnimatePresence>
        {selected.size > 0 && (
          <motion.div
            data-testid="bulk-action-bar"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3 shadow-xl"
          >
            <span className="text-sm font-semibold">{selected.size} valgt</span>
            <Button data-testid="bulk-approve-button" size="sm" onClick={() => bulk("godkjent")} className="gap-1.5 bg-[#16A34A] hover:bg-[#15803d] text-white">
              <Check className="h-4 w-4" /> Godkjenn valgte ({selected.size})
            </Button>
            <Button data-testid="bulk-reject-button" size="sm" variant="outline" onClick={() => bulk("avvist")} className="gap-1.5 text-destructive border-destructive/40">
              <X className="h-4 w-4" /> Avvis valgte
            </Button>
            <Button data-testid="bulk-clear-button" size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Nullstill valg
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Save search dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-card" data-testid="save-search-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <BookmarkPlus className="h-5 w-5 text-primary" /> Lagre søk
            </DialogTitle>
            <DialogDescription className="pt-1 text-left">
              Lagre område, bransjer og antall så du slipper å fylle inn hver gang.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder="F.eks. Asker kafeer"
            data-testid="save-search-name-input"
            className="bg-background"
            onKeyDown={(e) => e.key === "Enter" && saveSearch()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Avbryt</Button>
            <Button onClick={saveSearch} data-testid="save-search-confirm-button">Lagre</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send confirmation */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="bg-card" data-testid="send-modal-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Send className="h-5 w-5 text-[#16A34A]" /> Send {sendCount} e-poster
            </DialogTitle>
            <DialogDescription className="pt-2 text-left">
              {sendCount} e-poster er klare til å sendes fra din e-postkonto{" "}
              <b>{gmail?.email}</b>.
              {gmail?.connected
                ? " E-postene sendes direkte via din tilkoblede Gmail-konto."
                : gmail?.own_sending
                  ? " Gmail er ikke tilkoblet. Koble til bedriftens Gmail øverst før du kan sende."
                  : " Gmail er ikke tilkoblet ennå, så sendingen kjøres i SIMULERT MODUS (ingen ekte e-post går ut)."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button data-testid="send-modal-cancel-button" variant="outline" onClick={() => setSendOpen(false)}>
              AVBRYT
            </Button>
            <Button data-testid="send-modal-confirm-button" onClick={doSend} disabled={sending || (gmail?.own_sending && !gmail?.connected)} className="gap-1.5 bg-[#16A34A] hover:bg-[#15803d] text-white">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} SEND
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
